import React from 'react';
import { useQuery, useNavigate } from '@common/hooks';
import { Col, Row, Table } from 'antd';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import {
  Link,
  AddButton,
  AppContent,
  EmptyContent,
  ErrorHandler,
  Gutter,
  TextFilterSearch,
} from '@common/components';
import { WorksListTemplatesQuery } from 'works/legacy/graphql/works-list-template';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';

function formatWorksListTemplates(worksOnboardingListTemplate) {
  const dataHash = {};
  dataHash['key'] = worksOnboardingListTemplate.id;
  dataHash['name'] = worksOnboardingListTemplate.name;
  dataHash['compliance'] =
    worksOnboardingListTemplate.complianceDeadlineDetails;
  dataHash['canArchive'] = worksOnboardingListTemplate.canArchive.value;

  return dataHash;
}

export function OnboardingListTemplatesPage() {
  const navigate = useNavigate();
  const { loading, error, data } = useQuery(WorksListTemplatesQuery);

  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: 'Templates',
      path: '/works/admin/templates',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.LABEL,
      path: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE,
    },
  ]);

  if (error) {
    return <ErrorHandler error={error} log="OnboardingListTemplatesPage" />;
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <Gutter internal>
          <TextFilterSearch
            onSearch={(value) => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
          />
        </Gutter>
      ),
      onFilter: (value, record) =>
        record?.name?.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Compliance',
      dataIndex: 'compliance',
      key: 'compliance',
    },
  ];

  const dataSource = data?.worksListTemplates.map(formatWorksListTemplates);

  if (dataSource && dataSource.length === 0) {
    return (
      <AppContent>
        <Row justify="end">
          <Col>
            <Link to="/works/admin/templates/compliance-list/new">
              <AddButton>Compliance List Template</AddButton>
            </Link>
          </Col>
        </Row>
        <EmptyContent
          imagePath="./empty-templates.png"
          header="It's a bit empty in here..."
          text={
            <span>
              Start by adding a Compliance List Template above, or contact
              a&nbsp;
              <a
                href="mailto:support@workshealth.com?subject=Help: Configuring Compliance List Templates"
                target="blank"
              >
                <b>Works Specialist</b>
              </a>
              &nbsp;to configure Compliance List Templates for your Staffing
              Requests.
            </span>
          }
        />
      </AppContent>
    );
  }

  return (
    <AppContent className="onboarding-list-templates-page">
      <Row justify="end">
        <Col>
          <Link to="/works/admin/templates/compliance-list/new">
            <AddButton>Template</AddButton>
          </Link>
        </Col>
      </Row>
      <Table
        loading={loading}
        columns={columns}
        rowKey={(record) => record.key}
        dataSource={dataSource}
        onRow={(record) => ({
          onClick() {
            navigate(
              `/works/admin/templates/compliance-list/${record.key}/items`
            );
          },
        })}
      />
      <style jsx>{`
        :global(.onboarding-list-templates-page .ant-table-tbody > tr > td) {
          padding: 20px 4px 20px 8px;
          cursor: pointer;
        }
      `}</style>
    </AppContent>
  );
}
